import { useBreadcrumbs } from "@/hooks/Breadcrumbs/useBreadcrumbs"
import resourceUrl from "@/util/makeUrl"
import Link from "next/link"
import { string } from "prop-types"
import { Fragment, useMemo } from "react"
import { FormattedMessage } from "react-intl"
import Shimmer from "./breadcrumbs.shimmer"

import { formatString } from "@/lib/format-string"
import { cn } from "@/lib/utils"

const DELIMITER = "/"

/**
 * Breadcrumbs! Generates a sorted display of category links.
 *
 * @param {String} props.categoryId the uid of the category for which to generate breadcrumbs
 * @param {String} props.currentProduct the name of the product we're currently on, if any.
 */
const Breadcrumbs = (props) => {
  const { categoryId, currentProduct } = props

  const talonProps = useBreadcrumbs({
    categoryId,
  })

  const {
    currentCategory,
    currentCategoryPath,
    hasError,
    isLoading,
    normalizedData,
    handleClick,
  } = talonProps

  // For all links generate a fragment like "/ Text"
  const links = useMemo(() => {
    return normalizedData.map(({ text, path }) => {
      return (
        <Fragment key={text}>
          <span className={"p-1"}>{DELIMITER}</span>
          <Link
            className={"p-1 underline"}
            href={resourceUrl(path)}
            onClick={handleClick}
          >
            {text}
          </Link>
        </Fragment>
      )
    })
  }, [handleClick, normalizedData])

  if (isLoading) {
    return <Shimmer />
  }

  // Don't display anything but the empty, static height div when there's an error.
  if (hasError) {
    return (
      <div
        className={cn(
          "overflow-hidden",
          "text-ellipsis",
          "font-sans text-[12px] text-[#333] lg_text-[0.875rem]",
          "whitespace-nowrap"
        )}
        aria-live="polite"
        aria-busy="false"
      />
    )
  }

  // If we have a "currentProduct" it means we're on a PDP so we want the last
  // category text to be a link. If we don't have a "currentProduct" we're on
  // a category page so it should be regular text.
  const currentCategoryLink = currentProduct ? (
    <Link
      className={cn("p-1", "underline")}
      href={resourceUrl(currentCategoryPath)}
      onClick={handleClick}
    >
      {currentCategory}
    </Link>
  ) : (
    <span className={cn("p-1")}>{currentCategory}</span>
  )

  const currentProductNode = currentProduct ? (
    <Fragment>
      <span className={"p-1"}>{DELIMITER}</span>
      <span className={"p-1"}>{formatString(currentProduct)}</span>
    </Fragment>
  ) : null

  return (
    <div
      className={cn(
        "overflow-hidden",
        "text-ellipsis",
        "text-[12px]",
        "font-sans text-[#333] lg_text-[0.875rem]",
        "whitespace-nowrap"
      )}
      aria-live="polite"
      aria-busy="false"
    >
      <Link className={cn("p-1", "underline")} href="/">
        <FormattedMessage id={"global.home"} defaultMessage={"Home"} />
      </Link>
      {links}
      <span className={"p-1"}>{DELIMITER}</span>
      {currentCategoryLink}
      {currentProductNode}
    </div>
  )
}

export default Breadcrumbs

Breadcrumbs.propTypes = {
  categoryId: string.isRequired,
  currentProduct: string,
}
